import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalOverlay } from "@chakra-ui/modal";
import { getClient, createClient, updateClientById } from "api/graphQL_clients";
import { getAllOrganizationsSecond, getOrganization } from "api/graphQL_organizations";
import InputField from "components/fields/InputField";
import TextArea from "components/fields/TextArea";
import ClientPayment from "./ClientPayment";
import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { MdEdit, MdHighlightOff, MdInfo } from "react-icons/md";
import { Select as AntSelect } from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

interface iCreateOrgForm {
  name: string; org_id: string; description?: string; website?: string;
  email?: string; phone?: string; address?: string; city?: string;
  state?: string; country?: string; postal_code?: string; assigned_payment_methods?: any;
}

/* ── Step config ── */
const STEPS = [
  { key: "identity",  label: "Identity",  subtitle: "Organization & name",   icon: "user" },
  { key: "contact",   label: "Contact",   subtitle: "Email, phone & web",    icon: "contact" },
  { key: "address",   label: "Address",   subtitle: "Location details",      icon: "location" },
  { key: "payment",   label: "Payment Gateways",  subtitle: "Assign methods",        icon: "payment" },
];

const StepSVG = ({ icon, active, done }: { icon: string; active: boolean; done: boolean }) => {
  const col = done ? "#fff" : active ? "#fff" : "#94a3b8";
  const icons: Record<string, JSX.Element> = {
    user:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    contact:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    location: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    payment:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  };
  return icons[icon] ?? null;
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);

const ErrorMsg = ({ msg }: { msg?: string }) => (
  <p className={`wz-err${msg ? "" : " wz-err--hidden"}`} aria-live="polite">
    {msg && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
    {msg ?? ""}
  </p>
);

const CreateClientModal = ({
  fetchClients, id, info, roleData, allGateways, infoData,
}: {
  fetchClients: () => void; id?: string; info?: boolean;
  roleData?: any; allGateways?: any; infoData?: any;
}) => {
  const rootForm: iCreateOrgForm = {
    name: "", org_id: "", description: "", website: "", email: "",
    phone: "", address: "", city: "", state: "", country: "", postal_code: "", assigned_payment_methods: "",
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formValues, setFormValues] = React.useState<iCreateOrgForm>(rootForm);
  const [initialValues, setInitialFormValues] = React.useState<iCreateOrgForm>(rootForm);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = React.useState<boolean>(info);
  const [orgID, setOrgID] = React.useState<any>();
  const [paymentMethods, setPaymentMethods] = React.useState([]);
  const [checkedpaymentMethods, setCheckedPaymentMethods] = React.useState([]);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [methodName, setMethodName] = useState<boolean>(false);
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [isFetchingClient, setIsFetchingClient] = React.useState<boolean>(false);
  /* wizard state */
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [animDir, setAnimDir] = useState<"forward" | "back">("forward");
  const [animating, setAnimating] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverOpenE, setPopoverOpenE] = useState(false);
  const totalSteps = STEPS.length;

  const fetchOrganizations = () => {
    getAllOrganizationsSecond(1, 500, "")
      .then((data) => setOrganizations(data?.[0]?.map((ele: any) => ({ name: ele.name, id: ele.id })) ?? []))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Error fetching organizations"));
  };

  function fetchClient() {
    setIsFetchingClient(true);
    getClient(id)
      .then((data: any) => {
        setFormValues(data[0]); setInitialFormValues(data[0]); setOrgID(data[0].org_id);
        const selected = data[0]?.assigned_payment_methods?.map((ele: any) => {
          const gateway = allGateways?.find((item: any) => item.id === ele);
          return { value: ele, label: methodName ? gateway?.name : ele };
        });
        setCheckedPaymentMethods(selected ?? []);
      })
      .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch data"))
      .finally(() => setIsFetchingClient(false));
  }

  useEffect(() => {
    if (orgID !== undefined) {
      setMethodName(roleData?.[0]?.client?.value?.client_show_method_name);
      getOrganization({ id: orgID })
        .then((data: any) => setPaymentMethods(data[0]?.payment_methods))
        .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch data"));
    }
  }, [orgID]);

  const handleClose = () => {
    setIsCloseModal(true);
    setPopoverOpenE(false)
    setPopoverOpen(false)
    setTimeout(() => {
      setIsCloseModal(false); onClose();
      setFormValues(rootForm); setFormValuesErr({});
      setCurrentStep(0); setCompletedSteps(new Set()); setIsFetchingClient(false);
      setViewMode(info);
    }, 320);
  };

  const websiteRx = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d{1,5})?(\/.*)?$/;
  const emailRx   = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateStep = (step: number): boolean => {
    let errs: any = {};
    if (step === 0) {
      if (!formValues.name?.trim())   errs.name   = "Client name is required";
      if (!formValues.org_id)         errs.org_id = "Please select an organization";
    }
    if (step === 1) {
      if (formValues.website && !websiteRx.test(formValues.website)) errs.website = "Invalid website URL";
      if (formValues.email && !emailRx.test(formValues.email))       errs.email   = "Invalid email address";
    }
    if (step === 2) { /* address optional */ }
    if (step === 3) {
      const methods = formValues?.assigned_payment_methods;
      if (!methods || (Array.isArray(methods) ? methods.length === 0 : !methods)) {
        errs.assigned_payment_methods = "At least one payment method is required";
      }
    }
    setFormValuesErr((prev: any) => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const animateToStep = (next: number) => {
    if (animating) return;
    const dir = next > currentStep ? "forward" : "back";
    setAnimDir(dir);
    setAnimating(true);
    setTimeout(() => { setCurrentStep(next); setAnimating(false); }, 280);
  };

  const handleNext = () => {
    if (viewMode) { animateToStep(currentStep + 1); return; }
    if (!validateStep(currentStep)) return;
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    animateToStep(currentStep + 1);
  };

  const handleBack = () => { animateToStep(currentStep - 1); };

  const handleStepClick = (idx: number) => {
    if (viewMode || idx < currentStep || completedSteps.has(idx)) animateToStep(idx);
  };

  function handleValueChange(e: any) {
    const { id: fid, value } = e.target;
    let err = "";
    if (fid === "website" && value && !websiteRx.test(value)) err = "Invalid website URL";
    if (fid === "email"   && value && !emailRx.test(value))   err = "Invalid email address";
    setFormValuesErr((p: any) => ({ ...p, [fid]: err }));
    setFormValues((p: any) => ({ ...p, [fid]: value }));
  }

  function handleChildSelectionChange(selected: any) {
    setFormValues((p: any) => ({ ...p, assigned_payment_methods: selected?.map((e: any) => e.value) }));
    if (selected?.length > 0) setFormValuesErr((p: any) => ({ ...p, assigned_payment_methods: "" }));
  }

  const hasFormChanged = () => Object.keys(formValues).some((k) => formValues[k as keyof iCreateOrgForm] !== initialValues[k as keyof iCreateOrgForm]);

  async function handleSubmit() {
    if (!validateStep(3)) return;
    try {
      setIsLoading(true);
      if (id) {
        const update = await updateClientById(formValues);
        if (update.errors) toast.error(update.errors[0].message);
        else if (update[0]?.status === "Success") { toast.success("Client updated successfully"); fetchClients(); handleClose(); }
        else toast.error(update[0]?.message ?? "Something went wrong");
      } else {
        const create = await createClient(formValues);
        if (create.errors) toast.error(create.errors[0].message);
        else if (create[0]?.status === "Success") { toast.success("Client created successfully"); fetchClients(); handleClose(); }
        else toast.error(create[0]?.message ?? "Something went wrong");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Operation failed");
    } finally {
      setIsLoading(false);
    }
  }

  /* ── Trigger element ── */
  const TriggerEl = () => {
    if (info) {
      if (!roleData?.[0]?.client?.value?.view_client) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
          <PopoverTrigger>
            <div onClick={() => { if (id) fetchClient(); fetchOrganizations(); setViewMode(true); setCurrentStep(0); onOpen(); }} onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
              <div className="ccm-action-icon ccm-action-icon--blue"><MdInfo size={16} /></div>
            </div>
          </PopoverTrigger>
          <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="200px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View Client</PopoverHeader>
            <PopoverArrow bg="#eff6ff" />
          </PopoverContent>
        </Popover>
      );
    }
    if (id) {
      if (!roleData?.[0]?.client?.value?.edit_client) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpenE && !isOpen} onOpen={() => setPopoverOpenE(true)} onClose={() => setPopoverOpenE(false)}>
          <PopoverTrigger>
            <div onClick={() => { if (id) fetchClient(); fetchOrganizations(); setViewMode(false); setCurrentStep(0); onOpen(); }} onMouseEnter={() => !isOpen && setPopoverOpenE(true)} onMouseLeave={() => setPopoverOpenE(false)}>
              <div className="ccm-action-icon ccm-action-icon--amber"><MdEdit size={16} /></div>
            </div>
          </PopoverTrigger>
          <PopoverContent border="1px solid #fde68a" borderRadius="12px" bg="#fffbeb" maxW="200px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#fef3c7" fontSize="12px" fontWeight="700" color="#92400e" borderBottom="1px solid #fde68a" px="12px" py="9px">Edit Client</PopoverHeader>
            <PopoverArrow bg="#fffbeb" />
          </PopoverContent>
        </Popover>
      );
    }
    if (!roleData?.[0]?.client?.value?.add_client) return null;
    return (
      <button className="ccm-add-btn" onClick={() => { setViewMode(false); setCurrentStep(0); setCompletedSteps(new Set()); onOpen(); fetchOrganizations(); }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
        Add New Client
      </button>
    );
  };

  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="wz-step-body">
            <div className="wz-step-intro">
              <div className="wz-step-intro-icon wz-step-intro-icon--blue">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div>
                <h3 className="wz-step-heading">Client Identity</h3>
                <p className="wz-step-desc">Set the client's name and link them to an organisation.</p>
              </div>
            </div>
            <div className="wz-fields">
              <div className="wz-field-full wz-field-wrap">
                <label className="wz-label">Organization <span className="wz-req">*</span></label>
                <AntSelect
                  showSearch
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  allowClear
                  placeholder="Search and select organization…"
                  className={`wz-ant-select`}
                  disabled={viewMode}
                  options={organizations?.map((m: any) => ({ ...m, label: m?.name, value: m?.id }))}
                  value={formValues?.org_id || undefined}
                  onChange={(e: any) => {
                    setFormValues((p: any) => ({ ...p, org_id: e }));
                    setOrgID(e);
                    setFormValuesErr((p: any) => ({ ...p, org_id: "" }));
                  }}
                  notFoundContent="No organizations found"
                />
                {formValuesErr?.org_id && (
                  <ErrorMsg msg={formValuesErr?.org_id} />
                )}
              </div>
              <div className="wz-field-full wz-field-wrap">
                <label className="wz-label">Client Name <span className="wz-req">*</span></label>
                <input
                  type="text"
                  className={`wz-input`}
                  placeholder="e.g. Stripe Merchant, PayFlow Ltd…"
                  disabled={viewMode}
                  value={formValues?.name ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, name: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, name: "" }));
                  }}
                />
                {formValuesErr?.name && (
                  <ErrorMsg msg={formValuesErr?.name} />
                )}
              </div>
              <div className="wz-field-full wz-field-wrap">
                <label className="wz-label">Description <span className="wz-opt">(optional)</span></label>
                <textarea
                  rows={3}
                  className="wz-textarea"
                  placeholder="Brief description of this client…"
                  disabled={viewMode}
                  value={formValues?.description ?? ""}
                  onChange={(e) => setFormValues((p: any) => ({ ...p, description: e.target.value }))}
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="wz-step-body">
            <div className="wz-step-intro">
              <div className="wz-step-intro-icon wz-step-intro-icon--green">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div>
                <h3 className="wz-step-heading">Contact Details</h3>
                <p className="wz-step-desc">How customers and your team can reach this client.</p>
              </div>
            </div>
            <div className="wz-fields">
              <div className="wz-field-half">
                <label className="wz-label">Email <span className="wz-opt">(optional)</span></label>
                <div className="wz-input-icon-wrap">
                  <span className="wz-input-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </span>
                  <input
                    type="text"
                    id="email"
                    className={`wz-input wz-input--icon${formValuesErr?.email ? " wz-input--err" : ""}`}
                    placeholder="contact@company.com"
                    disabled={viewMode}
                    value={formValues?.email ?? ""}
                    onChange={handleValueChange}
                  />
                </div>
                {formValuesErr?.email && (
                  <ErrorMsg msg={formValuesErr?.email} />
                )}
              </div>
              <div className="wz-field-half">
                <label className="wz-label">Website <span className="wz-opt">(optional)</span></label>
                <div className="wz-input-icon-wrap">
                  <span className="wz-input-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  </span>
                  <input
                    type="text"
                    id="website"
                    className={`wz-input wz-input--icon${formValuesErr?.website ? " wz-input--err" : ""}`}
                    placeholder="www.company.com"
                    disabled={viewMode}
                    value={formValues?.website ?? ""}
                    onChange={handleValueChange}
                  />
                </div>
                {formValuesErr?.website && (
                  <ErrorMsg msg={formValuesErr?.website} />
                )}
              </div>
              <div className="wz-field-full">
                <label className="wz-label">Phone <span className="wz-opt">(optional)</span></label>
                <PhoneInput
                  country=""
                  disabled={viewMode}
                  value={formValues?.phone ?? ""}
                  onChange={(value) => setFormValues((p: any) => ({ ...p, phone: value }))}
                  inputStyle={{ width: "100%", height: "46px", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "14px", border: "1.5px solid #e2e8f0", borderRadius: "10px", background: viewMode ? "#f8fafc" : "#ffffff", color: "#0f172a" }}
                  buttonStyle={{ border: "1.5px solid #e2e8f0", borderLeft: "1.5px solid #e2e8f0", borderRadius: "10px 0 0 10px", background: "#f8fafc" }}
                  containerStyle={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="wz-step-body">
            <div className="wz-step-intro">
              <div className="wz-step-intro-icon wz-step-intro-icon--purple">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <h3 className="wz-step-heading">Address</h3>
                <p className="wz-step-desc">All address fields are optional but help with billing.</p>
              </div>
            </div>
            <div className="wz-fields">
              <div className="wz-field-full">
                <label className="wz-label">Address Line</label>
                <input
                  type="text"
                  className="wz-input"
                  placeholder="45 Test Street"
                  disabled={viewMode}
                  value={formValues?.address ?? ""}
                  onChange={(e) => setFormValues((p: any) => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div className="wz-field-half">
                <label className="wz-label">City</label>
                <input
                  type="text"
                  className="wz-input"
                  placeholder="Mountain View"
                  disabled={viewMode}
                  value={formValues?.city ?? ""}
                  onChange={(e) => setFormValues((p: any) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div className="wz-field-half">
                <label className="wz-label">State / Province</label>
                <input
                  type="text"
                  className="wz-input"
                  placeholder="California"
                  disabled={viewMode}
                  value={formValues?.state ?? ""}
                  onChange={(e) => setFormValues((p: any) => ({ ...p, state: e.target.value }))}
                />
              </div>
              <div className="wz-field-half">
                <label className="wz-label">Country</label>
                <input
                  type="text"
                  className="wz-input"
                  placeholder="USA"
                  disabled={viewMode}
                  value={formValues?.country ?? ""}
                  onChange={(e) => setFormValues((p: any) => ({ ...p, country: e.target.value }))}
                />
              </div>
              <div className="wz-field-half">
                <label className="wz-label">Postal Code</label>
                <input
                  type="text"
                  className="wz-input"
                  placeholder="850125"
                  disabled={viewMode}
                  value={formValues?.postal_code ?? ""}
                  onChange={(e) => setFormValues((p: any) => ({ ...p, postal_code: e.target.value }))}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="wz-step-body">
            <div className="wz-step-intro">
              <div className="wz-step-intro-icon wz-step-intro-icon--amber">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              </div>
              <div>
                <h3 className="wz-step-heading">Payment Gateways</h3>
                <p className="wz-step-desc">Select at least one payment gateway for this client.</p>
              </div>
            </div>
            <div className="wz-fields">
              <div className="wz-field-full">
                <ClientPayment
                  Organization={paymentMethods}
                  slectedOrg={undefined}
                  assignPaymentMethod={checkedpaymentMethods}
                  handleSelectionChange={handleChildSelectionChange}
                  errorclass={!!formValuesErr?.assigned_payment_methods}
                  methodName={methodName}
                  viewMode={viewMode}
                  allGateways={allGateways}
                  formData={formValues}
                />
                {formValuesErr?.assigned_payment_methods && (
                  <ErrorMsg msg={formValuesErr?.assigned_payment_methods} />
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === totalSteps - 1;
  const progressPct = ((currentStep) / (totalSteps - 1)) * 100;

  return (
    <>
      <TriggerEl />

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`wz-panel${isOpen && !isCloseModal ? " wz-panel--open" : " wz-panel--close"}`}>

          {/* ── Decorative top gradient bar ── */}
          <div className="wz-top-glow" />

          {/* ── Header ── */}
          <div className="wz-header">
            <div className="wz-header-brand">
              <div className="wz-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {viewMode ? <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#fff"/></> : id ? <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></> : <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>}
                </svg>
              </div>
              <div>
                <h2 className="wz-header-title">
                  {viewMode ? "Client Details" : id ? `Edit — ${infoData?.row?.original?.name ?? "Client"}` : "New Client"}
                </h2>
                <p className="wz-header-sub">Step {currentStep + 1} of {totalSteps} · {STEPS[currentStep].label}</p>
              </div>
            </div>
            <div className="wz-header-right">
              {viewMode && roleData?.[0]?.client?.value?.edit_client && (
                <button className="wz-edit-btn" onClick={() => setViewMode(false)}>
                  <MdEdit size={13} /> Switch to Edit
                </button>
              )}
              <button className="wz-close-btn" onClick={handleClose} aria-label="Close">
                <MdHighlightOff size={22} />
              </button>
            </div>
          </div>

          {/* ── Progress bar ── */}
          <div className="wz-progress-track">
            <div className="wz-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          {/* ── Step indicators ── */}
          <div className="wz-steps-row">
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              const isDone   = completedSteps.has(i);
              const isReach  = viewMode || i <= currentStep || completedSteps.has(i);
              return (
                <div key={step.key} className="wz-step-item" onClick={() => isReach && handleStepClick(i)}>
                  <div className={`wz-step-circle${isActive ? " wz-step-circle--active" : isDone ? " wz-step-circle--done" : " wz-step-circle--idle"}`}>
                    {isDone && !isActive ? <CheckIcon /> : <StepSVG icon={step.icon} active={isActive} done={isDone} />}
                    {isActive && <div className="wz-step-pulse" />}
                  </div>
                  <div className="wz-step-label-block">
                    <span className={`wz-step-label${isActive ? " wz-step-label--active" : isDone ? " wz-step-label--done" : ""}`}>{step.label}</span>
                    <span className="wz-step-sublabel">{step.subtitle}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`wz-step-connector${isDone ? " wz-step-connector--done" : ""}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Step content (animated) ── */}
          <div className="wz-content-area">
            {isFetchingClient ? (
              <div className="wz-skeleton-wrap">
                <div className="wz-skeleton-intro">
                  <div className="wz-skeleton wz-skeleton--icon" />
                  <div className="wz-skeleton-intro-text">
                    <div className="wz-skeleton wz-skeleton--title" />
                    <div className="wz-skeleton wz-skeleton--subtitle" />
                  </div>
                </div>
                <div className="wz-skeleton-fields">
                  <div className="wz-skeleton-field">
                    <div className="wz-skeleton wz-skeleton--label" />
                    <div className="wz-skeleton wz-skeleton--input" />
                  </div>
                  <div className="wz-skeleton-field">
                    <div className="wz-skeleton wz-skeleton--label" />
                    <div className="wz-skeleton wz-skeleton--input" />
                  </div>
                  <div className="wz-skeleton-field wz-skeleton-field--full">
                    <div className="wz-skeleton wz-skeleton--label" />
                    <div className="wz-skeleton wz-skeleton--textarea" />
                  </div>
                </div>
              </div>
            ) : (
              <div className={`wz-anim-wrap${animating ? ` wz-anim--exit-${animDir}` : ` wz-anim--enter-${animDir}`}`}>
                {renderStep(currentStep)}
              </div>
            )}
          </div>

          {/* ── Footer nav ── */}
          <div className="wz-footer">
            <div className="wz-footer-left">
              <div className="wz-dot-row">
                {STEPS.map((_, i) => (
                  <div key={i} className={`wz-dot${i === currentStep ? " wz-dot--active" : completedSteps.has(i) ? " wz-dot--done" : ""}`} />
                ))}
              </div>
              <span className="wz-footer-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                Encrypted &amp; secure
              </span>
            </div>
            <div className="wz-footer-btns">
              {currentStep > 0 && (
                <button className="wz-btn-back" onClick={handleBack}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  Back
                </button>
              )}
              <button className="wz-btn-cancel" onClick={handleClose}>Cancel</button>

              {isLastStep ? (
                !viewMode && (
                  <button className="wz-btn-submit" onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? (
                      <span className="wz-spinner" />
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        {id ? "Update Client" : "Create Client"}
                      </>
                    )}
                  </button>
                )
              ) : (
                <button className="wz-btn-next" onClick={handleNext} disabled={isFetchingClient}>
                  {viewMode ? "Next" : "Continue"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Trigger icons ── */
        .ccm-action-icon { width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;border:1.5px solid; }
        .ccm-action-icon--blue  { background:#eff6ff;border-color:#bfdbfe;color:#2563eb; }
        .ccm-action-icon--blue:hover  { background:#dbeafe;border-color:#93c5fd; }
        .ccm-action-icon--amber { background:#fffbeb;border-color:#fde68a;color:#d97706; }
        .ccm-action-icon--amber:hover { background:#fef3c7;border-color:#fcd34d; }

        .ccm-add-btn { display:inline-flex;align-items:center;gap:7px;font-family:'Plus Jakarta Sans',sans-serif;font-size:13.5px;font-weight:700;color:#fff;padding:10px 20px;border-radius:10px;border:none;background:linear-gradient(135deg,#1d4ed8,#2563eb);cursor:pointer;box-shadow:0 3px 12px rgba(37,99,235,.3);transition:transform .15s,box-shadow .18s; }
        .ccm-add-btn:hover { transform:translateY(-1px);box-shadow:0 6px 20px rgba(37,99,235,.38); }

        /* ── Wizard panel (slide-over) ── */
        .wz-panel {
          font-family:'Plus Jakarta Sans',sans-serif;
          position:fixed;top:0;right:0;bottom:0;
          width:100%;max-width:800px;
          background:#ffffff;
          z-index:1004;
          display:flex;flex-direction:column;
          box-shadow:-12px 0 60px rgba(15,23,42,.18);
          transition:transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow:hidden;
        }
        .wz-panel--open  { transform:translateX(0); }
        .wz-panel--close { transform:translateX(100%); }

        /* Top glow accent */
        .wz-top-glow { height:3px;background:linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744);background-size:200% 100%;animation:glowMove 3s linear infinite;flex-shrink:0; }
        @keyframes glowMove { 0%{background-position:0% 0%} 100%{background-position:200% 0%} }

        /* Header */
        .wz-header { padding:15px 26px;background:linear-gradient(135deg,#0f2744 0%,#1a3d6b 60%,#1d4ed8 100%);display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
        .wz-header-brand { display:flex;align-items:center;gap:13px; }
        .wz-header-logo { width:42px;height:42px;border-radius:12px;background:rgba(255,255,255,.15);border:1.5px solid rgba(255,255,255,.25);display:flex;align-items:center;justify-content:center;flex-shrink:0; }
        .wz-header-title { font-size:16px;font-weight:800;color:#fff;letter-spacing:-.3px;margin-bottom:2px;line-height:1; }
        .wz-header-sub { font-size:12px;color:rgba(255,255,255,.55);font-weight:500; }
        .wz-header-right { display:flex;align-items:center;gap:10px; }
        .wz-edit-btn { display:inline-flex;align-items:center;gap:5px;font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;font-weight:700;color:#fff;background:rgba(255,255,255,.14);border:1.5px solid rgba(255,255,255,.25);border-radius:8px;padding:7px 12px;cursor:pointer;transition:background .15s; }
        .wz-edit-btn:hover { background:rgba(255,255,255,.24); }
        .wz-close-btn { background:none;border:none;cursor:pointer;color:rgba(255,255,255,.65);display:flex;align-items:center;padding:2px;transition:color .15s; }
        .wz-close-btn:hover { color:#fff; }

        /* Progress bar */
        .wz-progress-track { height:4px;background:#e8edf3;flex-shrink:0; }
        .wz-progress-fill { height:100%;background:linear-gradient(90deg,#1d4ed8,#7c3aed);border-radius:0 4px 4px 0;transition:width .4s cubic-bezier(.4,0,.2,1); }

        /* Steps row */
        .wz-steps-row { display:flex;align-items:flex-start;padding:16px 22px 12px;background:#fafbfc;border-bottom:1px solid #f1f5f9;flex-shrink:0;overflow-x:auto;gap:0; }
        .wz-steps-row::-webkit-scrollbar { height:2px; }
        .wz-steps-row::-webkit-scrollbar-thumb { background:#e2e8f0; }

        .wz-step-item { display:flex;align-items:center;gap:0;flex:1;min-width:0;cursor:default; }

        /* Circle */
        .wz-step-circle { width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;position:relative;transition:all .25s; }
        .wz-step-circle--idle   { background:#f1f5f9;border:2px solid #e2e8f0; }
        .wz-step-circle--active { background:linear-gradient(135deg,#1d4ed8,#2563eb);border:2px solid transparent;box-shadow:0 0 0 4px rgba(37,99,235,.15); }
        .wz-step-circle--done   { background:linear-gradient(135deg,#15803d,#16a34a);border:2px solid transparent;box-shadow:0 0 0 3px rgba(22,163,74,.12); }

        /* Pulse ring on active */
        .wz-step-pulse { position:absolute;inset:-4px;border-radius:50%;border:2px solid rgba(37,99,235,.35);animation:stepPulse 1.8s ease-in-out infinite; }
        @keyframes stepPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0;transform:scale(1.4)} }

        .wz-step-label-block { display:flex;flex-direction:column;gap:1px;padding:0 6px;min-width:0; }
        .wz-step-label { font-size:11.5px;font-weight:600;color:#94a3b8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
        .wz-step-label--active { color:#1d4ed8;font-weight:800; }
        .wz-step-label--done   { color:#15803d;font-weight:700; }
        .wz-step-sublabel { font-size:10px;color:#cbd5e1;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }

        .wz-step-connector { flex:1;height:2px;background:#e2e8f0;margin:0 6px;margin-top:0px;border-radius:2px;transition:background .3s; }
        .wz-step-connector--done { background:linear-gradient(90deg,#16a34a,#22c55e); }

        /* Content area */
        .wz-content-area { flex:1;overflow-y:auto;overflow-x:hidden;position:relative; }
        .wz-content-area::-webkit-scrollbar { width:4px; }
        .wz-content-area::-webkit-scrollbar-thumb { background:#cbd5e1;border-radius:4px; }

        /* Animation classes */
        .wz-anim-wrap { transition:opacity .25s ease,transform .28s cubic-bezier(0.16,1,0.3,1); }
        .wz-anim--enter-forward { animation:slideInRight .28s cubic-bezier(0.16,1,0.3,1) both; }
        .wz-anim--enter-back    { animation:slideInLeft  .28s cubic-bezier(0.16,1,0.3,1) both; }
        .wz-anim--exit-forward  { animation:slideOutLeft .18s ease both; }
        .wz-anim--exit-back     { animation:slideOutRight .18s ease both; }
        @keyframes slideInRight  { from{opacity:0;transform:translateX(32px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes slideInLeft   { from{opacity:0;transform:translateX(-32px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slideOutLeft  { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(-24px)} }
        @keyframes slideOutRight { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(24px)} }

        /* Step body */
        .wz-step-body { padding:24px 28px 8px; }
        .wz-step-intro { display:flex;align-items:center;gap:14px;margin-bottom:24px; }
        .wz-step-intro-icon { width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0; }
        .wz-step-intro-icon--blue   { background:linear-gradient(135deg,#eff6ff,#dbeafe);border:1.5px solid #bfdbfe;color:#2563eb; }
        .wz-step-intro-icon--green  { background:linear-gradient(135deg,#f0fdf4,#dcfce7);border:1.5px solid #bbf7d0;color:#16a34a; }
        .wz-step-intro-icon--purple { background:linear-gradient(135deg,#faf5ff,#ede9fe);border:1.5px solid #ddd6fe;color:#7c3aed; }
        .wz-step-intro-icon--amber  { background:linear-gradient(135deg,#fffbeb,#fef3c7);border:1.5px solid #fde68a;color:#d97706; }
        .wz-step-heading { font-size:18px;font-weight:800;color:#0f172a;letter-spacing:-.3px;margin-bottom:3px;line-height:22px }
        .wz-step-desc { font-size:13px;color:#64748b;font-weight:500;line-height:1.5; }

        /* Form fields */
        .wz-fields { display:grid;grid-template-columns:1fr 1fr;column-gap:16px;row-gap:8px;align-items:start; }
        .wz-field-full  { grid-column:1/-1; }
        .wz-field-half  { grid-column:span 1; }
        /* wz-field-wrap: flex column so error text is always in flow, never overlapping */
        .wz-field-wrap  { display:flex;flex-direction:column; }


        .wz-label { font-size:12.5px;font-weight:700;color:#374151;margin-bottom:6px;display:block; }
        .wz-req   { color:#dc2626; }
        .wz-opt   { font-weight:500;color:#94a3b8;font-size:11.5px; }

        .wz-input {
          width:100%;padding:12px 14px;
          background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:11px;
          font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;color:#0f172a;
          outline:none;transition:border-color .18s,box-shadow .18s,background .18s;
          -webkit-appearance:none;
        }
        .wz-input::placeholder { color:#cbd5e1; }
        .wz-input:focus { border-color:#2563eb;background:#fff;box-shadow:0 0 0 3px rgba(37,99,235,.1); }
        .wz-input--err { border-color:#ef4444 !important;background:#fff7f7 !important;box-shadow:0 0 0 3px rgba(239,68,68,.08) !important; }
        .wz-input:disabled { background:#f1f5f9;color:#94a3b8;cursor:not-allowed; }

        .wz-input-icon-wrap { position:relative; }
        .wz-input-icon { position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#94a3b8;display:flex;align-items:center;pointer-events:none; }
        .wz-input--icon { padding-left:40px; }

        .wz-textarea {
          width:100%;padding:12px 14px;min-height:80px;
          background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:11px;
          font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;color:#0f172a;
          outline:none;resize:vertical;transition:border-color .18s,box-shadow .18s;
          -webkit-appearance:none;
        }
        .wz-textarea::placeholder { color:#cbd5e1; }
        .wz-textarea:focus { border-color:#2563eb;background:#fff;box-shadow:0 0 0 3px rgba(37,99,235,.1); }
        .wz-textarea:disabled { background:#f1f5f9;color:#94a3b8;cursor:not-allowed; }

        .wz-ant-select { width:100% !important;font-family:'Plus Jakarta Sans',sans-serif !important;display:table !important; }
        .wz-ant-select .ant-select-selector { height:46px !important;border:1.5px solid #e2e8f0 !important;border-radius:11px !important;background:#f8fafc !important; }
        .wz-ant-select .ant-select-selection-search-input { height:44px !important; }
        .wz-ant-select .ant-select-selection-placeholder { line-height:44px !important;color:#cbd5e1 !important; }
        .wz-ant-select .ant-select-selection-item { line-height:44px !important; }
        .wz-ant-select:not(.ant-select-disabled):hover .ant-select-selector { border-color:#93c5fd !important; }
        .wz-ant-select.ant-select-focused .ant-select-selector { border-color:#2563eb !important;box-shadow:0 0 0 3px rgba(37,99,235,.1) !important; }
        .wz-ant-select--err .ant-select-selector { border-color:#ef4444 !important;background:#fff7f7 !important;box-shadow:0 0 0 3px rgba(239,68,68,.08) !important; }

        .wz-err { display:flex;align-items:center;gap:5px;font-size:12px;color:#dc2626;font-weight:500;margin-top:5px;min-height:21px;line-height:1.4; }
        .wz-err--hidden { visibility:hidden; }

        /* Footer */
        .wz-footer {
          padding:14px 26px;
          border-top:1.5px solid #f1f5f9;
          background:#fafbfc;
          display:flex;align-items:center;justify-content:space-between;
          flex-wrap:wrap;gap:10px;flex-shrink:0;
        }
        .wz-footer-left { display:flex;align-items:center;gap:16px; }
        .wz-dot-row { display:flex;gap:6px;align-items:center; }
        .wz-dot { width:7px;height:7px;border-radius:50%;background:#e2e8f0;transition:all .2s; }
        .wz-dot--active { width:20px;border-radius:4px;background:linear-gradient(90deg,#1d4ed8,#2563eb); }
        .wz-dot--done   { background:#16a34a; }
        .wz-footer-hint { display:flex;align-items:center;gap:5px;font-size:12px;color:#94a3b8;font-weight:500; }

        .wz-footer-btns { display:flex;align-items:center;gap:8px; }

        .wz-btn-back {
          display:inline-flex;align-items:center;gap:6px;
          font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:600;color:#64748b;
          padding:9px 16px;border-radius:10px;border:1.5px solid #e2e8f0;background:#fff;
          cursor:pointer;transition:all .15s;
        }
        .wz-btn-back:hover { background:#f1f5f9;border-color:#cbd5e1;color:#374151; }

        .wz-btn-cancel {
          font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:600;color:#64748b;
          padding:9px 16px;border-radius:10px;border:1.5px solid #e2e8f0;background:#fff;
          cursor:pointer;transition:all .15s;
        }
        .wz-btn-cancel:hover { background:#f1f5f9;color:#374151; }

        .wz-btn-next {
          display:inline-flex;align-items:center;gap:7px;
          font-family:'Plus Jakarta Sans',sans-serif;font-size:13.5px;font-weight:700;color:#fff;
          padding:10px 22px;border-radius:10px;border:none;
          background:linear-gradient(135deg,#1d4ed8,#2563eb);
          cursor:pointer;
          box-shadow:0 4px 14px rgba(37,99,235,.3);
          transition:transform .15s,box-shadow .18s;
          position:relative;overflow:hidden;
        }
        .wz-btn-next::after { content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
        .wz-btn-next:hover { transform:translateY(-1px);box-shadow:0 8px 24px rgba(37,99,235,.4); }
        .wz-btn-next:active { transform:translateY(0); }

        .wz-btn-submit {
          display:inline-flex;align-items:center;gap:7px;
          font-family:'Plus Jakarta Sans',sans-serif;font-size:13.5px;font-weight:700;color:#fff;
          padding:10px 22px;border-radius:10px;border:none;
          background:linear-gradient(135deg,#15803d,#16a34a);
          cursor:pointer;
          box-shadow:0 4px 14px rgba(22,163,74,.3);
          transition:transform .15s,box-shadow .18s,opacity .18s;
          position:relative;overflow:hidden;
        }
        .wz-btn-submit::after { content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(255,255,255,.12),transparent 60%); }
        .wz-btn-submit:hover { transform:translateY(-1px);box-shadow:0 8px 24px rgba(22,163,74,.4); }
        .wz-btn-submit:disabled { opacity:.6;cursor:not-allowed;transform:none; }

        .wz-spinner { display:inline-block;width:16px;height:16px;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:wzSpin .65s linear infinite; }
        @keyframes wzSpin { to{transform:rotate(360deg)} }

        .wz-skeleton-wrap { padding:28px 28px 8px; }
        .wz-skeleton-intro { display:flex;align-items:center;gap:14px;margin-bottom:28px; }
        .wz-skeleton-intro-text { flex:1;display:flex;flex-direction:column;gap:8px; }
        .wz-skeleton-fields { display:flex;flex-direction:column;gap:20px; }
        .wz-skeleton-field { display:flex;flex-direction:column;gap:8px; }
        .wz-skeleton-field--full { width:100%; }
 
        .wz-skeleton { border-radius:8px;background:linear-gradient(90deg,#f1f5f9 25%,#e8edf5 50%,#f1f5f9 75%);background-size:400% 100%;animation:shimmer 1.5s ease-in-out infinite; }
        @keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:-100% 0} }
 
        .wz-skeleton--icon    { width:52px;height:52px;border-radius:14px;flex-shrink:0; }
        .wz-skeleton--title   { height:22px;width:180px;border-radius:6px; }
        .wz-skeleton--subtitle { height:14px;width:280px;border-radius:6px; }
        .wz-skeleton--label   { height:13px;width:120px;border-radius:4px; }
        .wz-skeleton--input   { height:46px;width:100%;border-radius:11px; }
        .wz-skeleton--textarea { height:88px;width:100%;border-radius:11px; }

        @media (max-width:640px) {
          .wz-panel { max-width:100%; }
          .wz-header { padding:16px 18px; }
          .wz-steps-row { padding:12px 14px 10px;gap:2px; }
          .wz-step-label-block { display:none; }
          .wz-step-body { padding:20px 18px 8px; }
          .wz-footer { padding:12px 18px; }
          .wz-fields { grid-template-columns:1fr; }
          .wz-field-half { grid-column:span 1; }
        }
      `}</style>
    </>
  );
};

export default CreateClientModal;